/**
 * useIsMobile — single source of truth for viewport breakpoint checks.
 *
 * Mobile-first shell foundations (Lot 0, mobile-first-shell-proposal.md §2d):
 * replaces the divergent one-shot `window.innerWidth` reads (BudgetsPage,
 * BudgetsDetails, FinanceEntityPage — audit finding B5) with a matchMedia
 * subscription, so the value stays correct after resize / orientation change
 * and is SSR-safe (server snapshot = desktop).
 *
 * Breakpoints follow the standard Tailwind scale (no custom values):
 *   - `MOBILE_BREAKPOINT` (640, Tailwind `sm`) — below it the shell renders
 *     mobile patterns: cards, bottom action bar, accordion tabs.
 *   - `TOUCH_DENSITY_BREAKPOINT` (768, Tailwind `md`) — below it the shell
 *     defaults to the `touch` density (44px targets, UPT-1286 precedent).
 *     Also the legacy Budget mobile boundary until the Lot 2 harmonisation.
 */
import { useCallback, useSyncExternalStore } from 'react';

/** Tailwind `sm` (px). Below this the shell is considered a mobile viewport. */
export const MOBILE_BREAKPOINT = 640;

/**
 * Tailwind `md` (px). Below this the shell defaults to `touch` density.
 * Also the historical Budget mobile boundary (kept until Lot 2 harmonisation).
 */
export const TOUCH_DENSITY_BREAKPOINT = 768;

const canMatchMedia = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/**
 * True when the viewport width is strictly below `breakpoint` pixels.
 *
 * @param breakpoint Width in px (defaults to `MOBILE_BREAKPOINT` = 640 / `sm`).
 */
export function useIsMobile(breakpoint: number = MOBILE_BREAKPOINT): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!canMatchMedia()) {
        return () => undefined;
      }

      const mediaQueryList = window.matchMedia(`(min-width: ${breakpoint}px)`);

      if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', onStoreChange);
        return () => mediaQueryList.removeEventListener('change', onStoreChange);
      }

      // Safari < 14 fallback
      mediaQueryList.addListener(onStoreChange);
      return () => mediaQueryList.removeListener(onStoreChange);
    },
    [breakpoint],
  );

  const getSnapshot = useCallback(() => {
    if (!canMatchMedia()) {
      return false;
    }
    return !window.matchMedia(`(min-width: ${breakpoint}px)`).matches;
  }, [breakpoint]);

  // SSR renders the desktop layout; the client corrects on hydration.
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export default useIsMobile;
